import { runtimeConfig } from '@/shared/config'
import { apiClient } from './client'

// Прямая ссылка на файл по key: бэк отдаёт 302 на presigned-URL (TTL 15 мин),
// <img>/<a> следуют редиректу. Годится и для src, и для download-href.
export function fileUrl(key: string): string {
  return `${runtimeConfig.API_BASE_URL}/files?key=${encodeURIComponent(key)}`
}

// RFC 7807 problem+json бэка → нормализованная ошибка. `code` (UPPER_SNAKE,
// напр. TASK_ALREADY_IN_WORK) — машинный, по нему подбираем человеческий текст.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    message?: string,
  ) {
    super(message ?? `API ${status}`)
    this.name = 'ApiError'
  }
}

function toApiError(status: number, data: unknown): ApiError {
  const problem = (data && typeof data === 'object' ? data : {}) as {
    code?: string
    title?: string
    detail?: string
  }
  return new ApiError(status, problem.code, problem.detail ?? problem.title)
}

// Разворачивает конверт { data }. Исключения ({ count }/{ affected }/
// { session, settings }) читаются точечно самим caller'ом мимо unwrap.
export function unwrap<T>(body: unknown): T {
  return (body as { data: T }).data
}

// Тонкая обёртка над untyped openapi-fetch (Paths = any до `pnpm gen:api`):
// путь интерполируем сами (`/tasks/${id}`), тело не-2xx ответа лежит в res.error.
// Возвращаем сырой payload (unknown) — caller разворачивает конверт и валидирует
// доменной Zod-схемой из entities/<x>/model/schema.ts.

export async function apiGet(path: string, query?: Record<string, string>): Promise<unknown> {
  const res = await apiClient.GET(path as never, (query ? { params: { query } } : {}) as never)
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return res.data
}

export async function apiPost(path: string, body?: unknown): Promise<unknown> {
  const res = await apiClient.POST(path as never, (body === undefined ? {} : { body }) as never)
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return res.data
}

export async function apiPatch(path: string, body?: unknown): Promise<unknown> {
  const res = await apiClient.PATCH(path as never, (body === undefined ? {} : { body }) as never)
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return res.data
}

export async function apiPut(path: string, body?: unknown): Promise<unknown> {
  const res = await apiClient.PUT(path as never, (body === undefined ? {} : { body }) as never)
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return res.data
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiClient.DELETE(path as never, {} as never)
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
}

// Загрузка файла — multipart: поле `file` + `kind ∈ {thread, focus}` (docs/stack/
// api.md). MIME на бэке не ограничен — размер/тип валидируем до вызова.
export async function apiUpload(
  path: string,
  file: File,
  fields?: Record<string, string>,
): Promise<unknown> {
  const form = new FormData()
  form.set('file', file)
  for (const [k, v] of Object.entries(fields ?? {})) form.set(k, v)
  const res = await apiClient.POST(
    path as never,
    {
      body: form,
      bodySerializer: (b: unknown) => b,
    } as never,
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return res.data
}
